import BlockCodeRuntime, {
  BlockCodeRuntimeProps,
} from '@/components/code/blockCode/BlockCodeRuntime';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import NumberInput from '@/components/ui/custom/dataInput/NumberInput';
import { jsFixGenerator } from '@/utils/blockQuestion';
import { blockCodeEditorSerializer, Workspace } from '@cna-br/astro-code';
import {
  Control,
  UseFormClearErrors,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { CodeFields } from './BlockCodeForm';
import { useChangeWorkspace } from './WorkspaceForm.hooks';

type WOrkspaceFormProps = {
  solutionJson: object;
  initialJson: object;
  control: Control<CodeFields>;
  setValue: UseFormSetValue<CodeFields>;
  clearErrors: UseFormClearErrors<CodeFields>;
};

export default function WorkspacesForm({
  control,
  clearErrors,
  setValue,
  solutionJson,
  initialJson,
}: WOrkspaceFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.activityBlock',
  });
  const {
    register,
    _formState: { errors },
  } = control;
  const onChangeSolution = (workspace: Workspace) => {
    setValue('solutionWorkspace', blockCodeEditorSerializer.save(workspace));
    setValue('solutionCode', jsFixGenerator(workspace));
    clearErrors('solutionCode');
  };

  const onChangeInitial = (workspace: Workspace) => {
    setValue('initialWorkspace', blockCodeEditorSerializer.save(workspace));
    setValue('initialCode', jsFixGenerator(workspace));
    clearErrors('initialCode');
  };
  const {
    workspace: solutionWorkspaceSvg,
    onWorkspaceChange: onSolutionWChange,
  } = useChangeWorkspace(solutionJson, onChangeSolution);

  const {
    workspace: initialWorkspaceSvg,
    onWorkspaceChange: onInitialWChange,
  } = useChangeWorkspace(initialJson, onChangeInitial);

  const maxBlocks = useWatch({ control, name: 'maxBlocks' });

  const validateMaxBlocks = (value?: number) => {
    const solutionBlocksSize = solutionWorkspaceSvg?.getAllBlocks().length || 0;
    const initialBlocksSize = initialWorkspaceSvg?.getAllBlocks().length || 0;
    if (!value) return true;
    if (value < solutionBlocksSize || value < initialBlocksSize) {
      return t('maxBlocksAlert');
    }
    return true;
  };

  return (
    <Fragment>
      <BlockCodeInput
        label={t('initial.label')}
        error={errors.initialCode?.message}
        testId="initial"
        workspace={initialWorkspaceSvg}
        maxBlocks={maxBlocks}
        onWorkspaceChange={onInitialWChange}
      />
      <BlockCodeInput
        label={t('solution.label')}
        error={errors.solutionCode?.message}
        testId="solution"
        workspace={solutionWorkspaceSvg}
        maxBlocks={maxBlocks}
        onWorkspaceChange={onSolutionWChange}
      />

      <NumberInput
        type="number"
        className={{
          input: 'w-16 h-8 disabled:bg-base-200',
          label: 'text-sm max-w-44',
        }}
        {...register('maxBlocks', {
          valueAsNumber: true,
          validate: validateMaxBlocks,
        })}
        label={t('maxBlocks')}
        errorLabelText={errors.maxBlocks?.message}
        labelPosition="left"
        testId="maxBlocks"
        showDecButton
        showIncButton
        onBlur={undefined}
        min={1}
      />
    </Fragment>
  );
}

type BlockCodeInputProps = {
  label: string;
  error?: string;
  testId: string;
} & BlockCodeRuntimeProps;

function BlockCodeInput({
  label,
  error,
  testId,
  workspace,
  onWorkspaceChange,
}: BlockCodeInputProps) {
  return (
    <div className="flex flex-col">
      <label
        htmlFor={testId}
        className="label leading-none p-0 w-full text-[0.875rem]"
      >
        {label}
      </label>
      <ConditionalRenderer condition={error}>
        <label className="font-rubik text-error error-label text-[0.875rem]">
          {error}
        </label>
      </ConditionalRenderer>
      <BlockCodeRuntime
        workspace={workspace}
        onWorkspaceChange={onWorkspaceChange}
        className="h-[18.75rem]"
      />
    </div>
  );
}
